import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { SEO } from '../../components/SEO';
import {
  School, TreePine, Library, MapPin, Users, Handshake, ArrowRight,
  Loader2, Building, ChevronDown, ChevronUp, GraduationCap,
  Factory, Globe, Cpu
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PartnersPage() {
  const [districts, setDistricts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [parks, setParks] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDistrict, setExpandedDistrict] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/partner/districts/`).catch(() => ({ data: { results: [] } })),
      axios.get(`${API_URL}/api/partner/schools/?page_size=100`).catch(() => ({ data: { results: [] } })),
      axios.get(`${API_URL}/api/partner/park-districts/`).catch(() => ({ data: { results: [] } })),
      axios.get(`${API_URL}/api/locations/?page_size=100`).catch(() => ({ data: { results: [] } })),
    ]).then(([distRes, schoolRes, parkRes, locRes]) => {
      const dists = distRes.data?.results || distRes.data || [];
      const schs = schoolRes.data?.results || schoolRes.data || [];
      const pks = parkRes.data?.results || parkRes.data || [];
      const locs = locRes.data?.results || locRes.data || [];

      // Order districts: SD204, SD308, SD129, SD131
      const order = ['SD204', 'SD308', 'SD129', 'SD131'];
      dists.sort((a, b) => {
        const ai = order.indexOf(a.code); const bi = order.indexOf(b.code);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });

      setDistricts(dists.map(d => ({ ...d, schools: schs.filter(s => s.district === d.id) })));
      setParks(pks);
      setLocations(locs.filter(l => l.location_category === 'library'));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const totalSchools = districts.reduce((sum, d) => sum + (d.schools?.length || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-rsc-navy" />
      </div>
    );
  }

  return (
    <div data-testid="partners-page">
      <SEO title="Our Partners" description="Ready Set STEM partners with school districts, park districts, and libraries across the Fox Valley region to bring STEM education to every child." />

      {/* Hero */}
      <section className="relative py-16 bg-gradient-to-br from-rsc-navy via-[#1a3a6d] to-[#0f2847] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(251,191,36,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.2) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="container-app relative z-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-sm font-medium mb-4">
              <Handshake className="w-4 h-4" /> COMMUNITY PARTNERSHIPS
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] mb-3">
              Building STEM Together<span className="text-rsc-yellow">.</span>
            </h1>
            <p className="text-base text-white/60 max-w-xl mb-6">
              We partner with school districts, park districts, and libraries across the Fox Valley region to bring hands-on STEM education directly to students in their communities.
            </p>
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-3xl font-extrabold text-rsc-yellow">{totalSchools}</p>
                <p className="text-xs text-white/40">Schools Served</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-extrabold text-white">{districts.length}</p>
                <p className="text-xs text-white/40">School Districts</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-extrabold text-white">{parks.length}</p>
                <p className="text-xs text-white/40">Park Districts</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-extrabold text-white">{locations.length}</p>
                <p className="text-xs text-white/40">Library Partners</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* School Districts */}
      <section className="py-12 bg-white">
        <div className="container-app">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <School className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">School District Partners</h2>
              <p className="text-sm text-gray-500">{districts.length} districts · {totalSchools} schools served</p>
            </div>
          </div>

          <div className="space-y-4">
            {districts.map(district => {
              const isExpanded = expandedDistrict === district.id;
              const schoolCount = district.schools?.length || 0;
              const elementary = district.schools?.filter(s => s.name.includes('Elementary')) || [];
              const middle = district.schools?.filter(s => s.name.includes('Middle') || s.name.includes('Junior')) || [];

              return (
                <Card key={district.id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`district-${district.code}`}>
                  <button
                    className="w-full p-5 flex items-center justify-between text-left"
                    onClick={() => setExpandedDistrict(isExpanded ? null : district.id)}
                  >
                    <div className="flex items-center gap-4">
                      {district.logo_url ? (
                        <img src={district.logo_url} alt={district.name} className="w-14 h-14 rounded-xl object-contain bg-white border border-gray-100 p-1 flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <GraduationCap className="w-7 h-7 text-blue-600" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{district.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <Badge variant="outline" className="text-xs">{district.code}</Badge>
                          <span>{schoolCount} school{schoolCount !== 1 ? 's' : ''}</span>
                          {elementary.length > 0 && <span className="text-blue-500">{elementary.length} elementary</span>}
                          {middle.length > 0 && <span className="text-indigo-500">{middle.length} middle</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700 text-xs">{schoolCount} schools</Badge>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 border-t bg-gray-50/50">
                      {elementary.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Elementary Schools</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {elementary.map(s => (
                              <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg bg-white border border-gray-100">
                                <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                                <span className="text-sm text-gray-700">{s.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {middle.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Middle Schools</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {middle.map(s => (
                              <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg bg-white border border-gray-100">
                                <div className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" />
                                <span className="text-sm text-gray-700">{s.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Park Districts */}
      <section className="py-12 bg-gray-50">
        <div className="container-app">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <TreePine className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Park District Partners</h2>
              <p className="text-sm text-gray-500">Summer camp programs through local park districts</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {parks.map(park => (
              <Card key={park.id} className="hover:shadow-md transition-shadow" data-testid={`park-${park.id}`}>
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                    <TreePine className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{park.name}</h3>
                    <p className="text-xs text-gray-500">Summer Camp Partner</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Libraries */}
      <section className="py-12 bg-white">
        <div className="container-app">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Library className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Library Partners</h2>
              <p className="text-sm text-gray-500">Half-day STEM camps at local libraries</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {locations.map(lib => (
              <Card key={lib.id} className="hover:shadow-md transition-shadow" data-testid={`library-${lib.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <Library className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{lib.name}</h3>
                      <p className="text-xs text-gray-500">{lib.address}, {lib.city}, {lib.state} {lib.zip_code}</p>
                      {lib.schedule_notes && <p className="text-xs text-purple-500 mt-1">{lib.schedule_notes}</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Partnerships */}
      <section className="py-12 bg-gray-50" data-testid="partner-section">
        <div className="container-app">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-navy/20 bg-rsc-navy/5 text-rsc-navy text-xs font-medium mb-3">
              <Handshake className="w-3.5 h-3.5" /> INDUSTRY PARTNERSHIPS
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-rsc-navy mb-2">
              Partner With <span className="text-rsc-yellow">Ready Set STEM</span>
            </h2>
            <p className="text-gray-500 max-w-lg mx-auto text-sm">
              Your investment connects students to careers and your company to a pipeline of trained, certified talent.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { tier: 'Platinum', investment: '$25K+ / year', accent: 'bg-gray-100 border-gray-300', examples: 'Major manufacturers, FANUC, universities' },
              { tier: 'Gold', investment: '$10K–$25K / year', accent: 'bg-rsc-yellow/10 border-rsc-yellow/30', examples: 'Regional manufacturers, logistics, tech' },
              { tier: 'Silver', investment: '$5K–$10K / year', accent: 'bg-gray-50 border-gray-200', examples: 'Small-medium businesses, associations' },
              { tier: 'Community', investment: 'In-kind / Volunteering', accent: 'bg-emerald-50 border-emerald-200', examples: 'Chamber of commerce, civic orgs, media' },
            ].map((tier, i) => (
              <div key={i} className={`p-4 rounded-xl border ${tier.accent}`} data-testid={`partner-tier-${tier.tier.toLowerCase()}`}>
                <h3 className="text-sm font-bold text-rsc-navy mb-0.5">{tier.tier}</h3>
                <p className="text-rsc-yellow font-semibold text-xs mb-1.5">{tier.investment}</p>
                <p className="text-[10px] text-gray-500">{tier.examples}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <h3 className="text-sm font-bold text-rsc-navy mb-2">Target Partner Categories</h3>
              <div className="space-y-1.5">
                {[
                  { icon: Factory, label: 'Advanced Manufacturing', desc: 'Caterpillar, John Deere, Illinois Tool Works' },
                  { icon: Globe, label: 'Logistics & Warehousing', desc: 'Amazon, FedEx, UPS, regional 3PL' },
                  { icon: Cpu, label: 'Technology', desc: 'Motorola Solutions, Argonne, Fermilab' },
                  { icon: GraduationCap, label: 'Higher Education', desc: 'Waubonsee CC, NIU, U of I system' },
                ].map((p, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-white border border-gray-200">
                    <p.icon className="w-3.5 h-3.5 text-rsc-yellow mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-rsc-navy">{p.label}</p>
                      <p className="text-[10px] text-gray-400">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-5 rounded-xl border border-gray-200 bg-white">
              <h3 className="text-sm font-bold text-rsc-navy mb-1">Become an Industry Partner</h3>
              <p className="text-gray-500 text-xs mb-4">Interested in sponsoring or mentoring? Get in touch and we'll walk you through partnership opportunities.</p>
              <Link to="/contact">
                <Button className="w-full bg-rsc-yellow text-rsc-navy font-bold rounded-full text-sm">
                  Contact Us <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Become a Partner CTA */}
      <section className="py-14 bg-gradient-to-br from-rsc-navy to-[#0f2847]">
        <div className="container-app text-center">
          <Handshake className="w-10 h-10 text-rsc-yellow mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Interested in Partnering?</h2>
          <p className="text-white/50 max-w-md mx-auto mb-6 text-sm">
            Whether you're a school district, park district, library, or community organization — we'd love to bring STEM education to your students.
          </p>
          <Link to="/contact">
            <Button className="bg-rsc-yellow text-rsc-navy font-bold rounded-full px-8 text-sm">
              Get in Touch <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
